import { FlexColumn, FlexSpace, SelectField } from '@ubeya/shared-web/components';
import React, { useCallback, useContext, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useOrgVenues from '@ubeya/shared/hooks/account/useOrgVenues';
import useOrgContracts from '@ubeya/shared/hooks/account/useOrgContracts';
import useCanAccessOrgDashboard from '@ubeya/shared/hooks/account/useCanAccessOrgDashboard';
import LocationModalFields from './LocationModalFields';

const LocationFieldsContainer = styled(FlexColumn)`
  gap: 20px;
`;

const Container = styled(FlexSpace)`
  width: 100%;
`;

const LocationFields = ({
  selectedLocation,
  showBranchSelector = false,
  editableAddress = false,
  disableFieldsEdit = false,
  disableBranchEdit = false,
  branchesOptions = [],
  // this modal is shared by branch info, scheduling, the client card and the location
  // selector; only the branch settings screen persists org assignments, so the fields are
  // opt-in rather than appearing wherever the modal is reused
  showOrgAssignments = false
}) => {
  const { t } = useContext(ConfigContext);
  const { config } = useAccount();
  const { orgVenues: venues } = useOrgVenues({ attachedToAccount: true });
  const { orgContracts: contracts, orgContractsOptions: contractOptions } = useOrgContracts();
  const canAccessOrg = useCanAccessOrgDashboard();
  const initialCenterLocation = { lat: selectedLocation?.lat || 0, lng: selectedLocation?.lng || 0 };

  const {
    input: { value: addressValue, onChange: setAddress }
  } = useField('address');
  const {
    input: { onChange: setLocation }
  } = useField('location');

  const venueOptions = useMemo(() => venues.map(({ id, name }) => ({ value: id, label: name })), [venues]);

  const isOrgAssignable =
    showOrgAssignments && canAccessOrg && !!config?.isOrganizationDashboardEnabled && !!config?.organizationId;

  const showVenueField = isOrgAssignable && venues.length > 0;

  // contracts are org-wide, so the field appears as soon as the organization has any
  const showContractField = isOrgAssignable && contracts.length > 0;

  // picking a venue inherits its address onto the location (and recenters the map through
  // the location field) only when the location has no address of its own — an address already
  // entered on the location wins over the venue's. Clearing the venue leaves the address as is.
  const onVenueChange = useCallback(
    (value, input) => {
      input.onChange(value);

      if (addressValue) {
        return;
      }

      const { address } = venues.find(({ id }) => id === value) || {};
      if (!address?.name) {
        return;
      }

      setAddress(address.name);
      if (address.lat && address.lng) {
        setLocation({ lat: address.lat, lng: address.lng });
      }
    },
    [addressValue, venues, setAddress, setLocation]
  );

  return (
    <LocationFieldsContainer>
      <Container>
        <LocationModalFields
          selectedLocation={selectedLocation}
          showBranchSelector={showBranchSelector}
          editableAddress={editableAddress}
          branchesOptions={branchesOptions}
          disableFieldsEdit={disableFieldsEdit}
          disableBranchEdit={disableBranchEdit}
          initialCenterLocation={initialCenterLocation}
          afterNameField={
            <>
              {showVenueField && (
                <SelectField
                  name="orgVenueId"
                  title={t('orgVenue')}
                  options={venueOptions}
                  isMulti={false}
                  clearable
                  onChange={onVenueChange}
                />
              )}
              {showContractField && (
                <SelectField
                  name="orgContractId"
                  title={t('orgContract')}
                  options={contractOptions}
                  isMulti={false}
                  clearable
                />
              )}
            </>
          }
        />
      </Container>
    </LocationFieldsContainer>
  );
};

export default LocationFields;
